import { useRef, useState } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { throttle } from "lodash";
import useDeepCompareEffect from "use-deep-compare-effect";

export type AsyncRequestFunction<T, R extends unknown[]> = (...params: R) => Promise<T>;

export interface AsyncResponse<T> {
  loading: boolean;
  refresh: () => void;
  data?: T;
  error?: Error;
}

function useAsyncRequest<T, R extends unknown[]>(func: AsyncRequestFunction<T, R>, ...params: R): AsyncResponse<T> {
  const { inProgress } = useMsal();

  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  // Store the function in a ref so the throttled function can access the latest version
  const funcRef = useRef(func);
  funcRef.current = func;

  // Only the latest request may update state, so a slow response to an older one can't replace it.
  const latestRequestId = useRef(0);

  // throttles api calls e.g. only 1 api call will be made within the set delay
  const throttled = useRef(
    throttle((params) => {
      const requestId = ++latestRequestId.current;
      const isLatest = () => requestId === latestRequestId.current;
      setLoading(true);
      setError(undefined);
      // Use funcRef.current to always get the latest function
      funcRef
        .current(...params)
        .then((result) => {
          if (isLatest()) {
            setData(result);
          }
        })
        .catch((ex) => {
          if (isLatest()) {
            setError(ex);
          }
        })
        .finally(() => {
          if (isLatest()) {
            setLoading(false);
          }
        });
    }, 1000)
  );

  useDeepCompareEffect(() => {
    if (inProgress === InteractionStatus.None) {
      throttled.current(params);
    }

    return () => {
      latestRequestId.current++;
      throttled.current.cancel();
    };
  }, [func, inProgress, params, shouldRefresh]);

  return {
    data,
    refresh: () => setShouldRefresh(!shouldRefresh),
    loading,
    error,
  };
}

export default useAsyncRequest;
